import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface MergeTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  availableSourceColumns: string[];
  fileData: Record<string, string[]>; // Map of column name to sample data array
  onApplyTransformation: (config: any) => void;
}

const MergeTransformationDialog: React.FC<MergeTransformationDialogProps> = ({
  isOpen,
  onClose,
  targetField,
  availableSourceColumns,
  fileData,
  onApplyTransformation,
}) => {
  const [formData, setFormData] = useState({
    sourceColumns: [] as string[],
    separator: " ",
  });

  const [previewData, setPreviewData] = useState<{
    before: Record<string, string[]>;
    after: string[];
  }>({
    before: {},
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      setPreviewData({ before: fileData, after: [] });
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "sourceColumns",
      type: "multi-select",
      label: "Columns to Merge",
      options: availableSourceColumns.map((col) => ({
        value: col,
        label: col,
      })),
      helperText: "Select columns to combine (in order)",
      className: "col-span-1",
    },
    {
      name: "separator",
      type: "text",
      label: "Separator",
      placeholder: "e.g., space, comma, hyphen",
      helperText: "Character(s) to insert between merged values (optional)",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);

    if (fieldName === "sourceColumns" || fieldName === "separator") {
      updatePreview(newFormData.sourceColumns, newFormData.separator);
    }
  };

  const updatePreview = (columns: string[], separator: string) => {
    if (!columns || columns.length === 0) {
      setPreviewData((prev) => ({ ...prev, after: [] }));
      return;
    }

    const maxSamples = 2;
    const mergedResults: string[] = [];

    for (let i = 0; i < maxSamples; i++) {
      const values = columns.map((col) =>
        fileData[col]?.[i] ? fileData[col][i] : ""
      );
      mergedResults.push(values.filter((v) => v).join(separator || ""));
    }

    setPreviewData((prev) => ({ ...prev, after: mergedResults }));
  };

  const handleApply = () => {
    if (formData.sourceColumns.length === 0) return;

    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "merge",
        order: 1,
        config: {
          source_columns: formData.sourceColumns.map(toSnakeCase),
          separator: formData.separator,
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Merge Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        {/* Info Banner */}
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Merge Data:</strong> Combine multiple columns into one.
            Perfect for creating full names, addresses, or composite IDs.
          </div>
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Live Preview Section */}
        {formData.sourceColumns.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-3 text-gray-900">Preview</h4>

              <div className="grid grid-cols-2 gap-6">
                {/* Before Columns */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Before
                  </p>
                  <div className="space-y-2">
                    {formData.sourceColumns.map((col) => (
                      <div
                        key={col}
                        className="bg-white p-3 rounded-md border border-gray-200"
                      >
                        <div className="text-xs text-gray-500 mb-1">{col}:</div>
                        <div className="text-sm text-gray-800">
                          {fileData[col]?.slice(0, 2).join(", ") || "No data"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* After Column */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    After
                  </p>
                  <div className="space-y-2">
                    {previewData.after.map((merged, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-md border border-gray-200"
                      >
                        <div className="text-sm">
                          <span className="text-gray-500">{targetField}:</span>{" "}
                          <span className="text-gray-800 font-medium">
                            {merged || "(empty)"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={formData.sourceColumns.length === 0}
          >
            Apply Transformation
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default MergeTransformationDialog;
